import type { EditorFeature } from '../models/editor-feature';

/**
 * How many desks in this selection have a real workplace behind them.
 *
 * A desk is bookable because a `Workplaces` row exists for its featureId, and that row is
 * what carries the seat's check-ins and reservations. A desk the editor has just drawn has
 * none of that yet.
 */
export const bookableDeskCountOf = (
  features: readonly EditorFeature[],
  bookableFeatureIds: ReadonlySet<string>,
): number => features
  .filter((feature) => feature.kind === 'desk' && bookableFeatureIds.has(feature.featureId))
  .length;

/**
 * Whether this selection may be deleted at all.
 *
 * More than one bookable desk at a time is refused. Deleting one is a considered act with a
 * confirmation in front of it; deleting forty is the same act repeated by a rubber-band drag
 * nobody meant to make, and it takes forty rooms' worth of capacity and every reservation
 * hanging off those workplaces with it. There is no undo once the plan is saved.
 *
 * Everything else stays freely deletable, including a block of freshly drawn desks: those
 * have no workplace behind them, so nothing is lost but the drawing. A single bookable desk
 * alongside any number of labels and shapes is also still fine — the limit is on how many
 * real seats one keystroke can remove, not on selection size.
 */
export const canDeleteSelection = (
  features: readonly EditorFeature[],
  bookableFeatureIds: ReadonlySet<string>,
): boolean => bookableDeskCountOf(features, bookableFeatureIds) <= 1;

export default canDeleteSelection;
